import { AsyncAutocompleteSource } from '@components/v2/form/AsyncAutocomplete/asyncAutocomplete.types';
import Modal from '@components/v2/ui/Modal/Modal';
import * as R from 'ramda';
import { memo, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import usePath from '../../../components/Hooks/usePath';
import useGoBackUrl from '../../../hooks/useGoBackUrl';
import useHandleBrandContextInSessionStorage from '../../../hooks/useHandleBrandContextInSessionStorage';
import LangEnum from '../../../lang/LangEnum';
import PATHS from '../../../routes/routes.const';
import { useFindCustomersToCopy } from '../../../services/modules/orders/orders.queries';
import useGeneralSettingsStore from '../../../stores/GeneralSettings/useGeneralSettingsStore';
import { useApplicationContext } from '../../context/ApplicationContext/ApplicationContext';
import { useTranslation } from '../../translation/TranslationContext';
import { ActionEnum } from '../actions/types/orderActions.types';
import { useCopyOrder } from '../hooks/useCopyOrder';
import { useCopyOrdersBatch } from '../hooks/useCopyOrdersBatch';
import { useGetOrderTotals } from '../hooks/useOrderTotals/order-totals.queries';
import CopyOrderContent from './components/CopyOrderContent';
import { useCopyOrderFooter } from './hooks/useCopyOrderFooter';
import {
  CopyOrderSuggestedOrderDraftModalProps,
  CustomerToCopy,
} from './types/copyOrderModal.types';

const CopyOrderSuggestedOrDraftModal = ({
  isOpen,
  onClose,
  reload,
  type,
  batchUpdateOrdersParams,
}: CopyOrderSuggestedOrderDraftModalProps) => {
  const { translate } = useTranslation();
  const { isStore } = useApplicationContext();
  const { isPath } = usePath();
  const isOrderListPath = isPath(PATHS.main.orders.path);
  const history = useHistory();
  const goBackUrl = useGoBackUrl(PATHS.main.orders.path);

  const isBatchMode = !!batchUpdateOrdersParams;

  const individualCopy = useCopyOrder({ reload, onClose });
  const batchCopy = useCopyOrdersBatch({
    batchUpdateOrdersParams: batchUpdateOrdersParams ?? {
      orderIds: [],
      allOrdersFromMassActions: false,
    },
    onClose,
    reload,
  });

  const onCopy = isBatchMode ? batchCopy.onCopyOrders : individualCopy.onCopyOrder;
  const invalidCustomerIds = isBatchMode
    ? batchCopy.invalidCustomerIds
    : individualCopy.invalidCustomerIds;
  const brandId = isBatchMode ? undefined : individualCopy.brandId;

  const { refetch: refetchOrderTabs } = useGetOrderTotals();

  const copyAsDraft = type === ActionEnum.COPY_AS_DRAFT;
  const copyAsSuggested = type === ActionEnum.COPY_AS_SUGGESTED;

  const getModalTitle = () => {
    if (isBatchMode) {
      return translate(
        copyAsDraft
          ? LangEnum.ORDER_MASS_ACTIONS_COPY_ORDERS_LABEL
          : LangEnum.ORDER_MASS_ACTIONS_COPY_ORDERS_AS_SUGGESTED_LABEL,
      );
    }
    if (copyAsDraft) {
      return translate(LangEnum.SUGGESTED_ORDER_COPY_TITLE_DRAFT);
    }
    return translate(LangEnum.SUGGESTED_ORDER_COPY_TITLE_SUGGESTED);
  };

  useHandleBrandContextInSessionStorage({ brandId, enabled: isOpen });

  const customersSource: AsyncAutocompleteSource = {
    url: '/orders/info/customers-to-copy',
    limit: 20,
    defaultQuery: { brandId },
  };

  const forceCopyOrderWithConditions = useGeneralSettingsStore(
    (state) => state.generalSettings.forceCopyOrderWithConditions,
  );

  const [selectedCustomers, setSelectedCustomers] = useState<CustomerToCopy[]>([]);
  const [gridMultiplier, setGridMultiplier] = useState(1);
  const [copyOrderWithConditions, setCopyOrderWithConditions] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [keepOriginalCustomer, setKeepOriginalCustomer] = useState(isBatchMode && !isStore);

  const hasInvalidCustomers = invalidCustomerIds.some((invalidId) =>
    selectedCustomers.some((customer) => customer.id === invalidId),
  );

  const handleChangeKeepOriginalCustomer = (value: boolean) => {
    setKeepOriginalCustomer(value);
    if (value) {
      setSelectedCustomers([]);
    }
  };

  const handleSubmit = async () => {
    setIsSaving(true);

    try {
      const copySucceeded = await onCopy({
        copyOrderWithConditions,
        customerGroups: keepOriginalCustomer ? [] : selectedCustomers,
        asSuggested: copyAsSuggested,
        gridMultiplier,
        keepOriginalCustomer: isBatchMode ? keepOriginalCustomer : undefined,
      });
      if (!copySucceeded) {
        return;
      }

      if (isOrderListPath) {
        await refetchOrderTabs();
      } else {
        history.push(goBackUrl);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const onChangeInsertGridMultiplier = (value: number) => {
    setGridMultiplier(value);
  };

  const { data: customersToCopy } = useFindCustomersToCopy(
    { limit: 1, brandId },
    { enabled: isOpen && isStore && !keepOriginalCustomer },
  );

  useEffect(() => {
    const [onlyCustomer] = customersToCopy?.data ?? [];
    if (isStore && !keepOriginalCustomer && customersToCopy?.count === 1 && onlyCustomer) {
      setSelectedCustomers([onlyCustomer]);
    }
  }, [isStore, keepOriginalCustomer, customersToCopy]);

  const footer = useCopyOrderFooter({
    isSaving,
    isSuggested: copyAsSuggested,
    selectedCustomersCount: selectedCustomers.length,
    hasInvalidCustomers,
    keepOriginalCustomer,
    onCancel: onClose,
    onConfirm: handleSubmit,
    confirmLabel:
      R.isEmpty(selectedCustomers) && !keepOriginalCustomer
        ? LangEnum.SUGGESTED_ORDER_COPY_ACTION_WITHOUT_STORE
        : LangEnum.GLOBAL_COPY,
  });

  return (
    <Modal
      isOpen={isOpen}
      size="expanded"
      header={{ title: getModalTitle() }}
      footer={footer}
      onClose={onClose}
    >
      <CopyOrderContent
        copyAsDraft={copyAsDraft}
        customers={selectedCustomers}
        source={customersSource}
        setCustomers={setSelectedCustomers}
        copyOrderWithConditions={copyOrderWithConditions}
        disableCopyOrderWithConditions={forceCopyOrderWithConditions}
        onChangeInsertGridMultiplier={onChangeInsertGridMultiplier}
        onChangeCopyOrderWithConditions={setCopyOrderWithConditions}
        invalidCustomerIds={invalidCustomerIds}
        isBatchMode={isBatchMode}
        keepOriginalCustomer={keepOriginalCustomer}
        onChangeKeepOriginalCustomer={handleChangeKeepOriginalCustomer}
      />
    </Modal>
  );
};

export default memo(CopyOrderSuggestedOrDraftModal);
